import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {latestOpsAsOf, OpsStatusInfoPanelView, type OpsStatusView} from "@ots-frontend/ots-ui-tw";
import {getRefServiceStatusDisplayValue} from "../../../../../util/ref-service-status-ui-util";
import {getRefServiceModeDisplayValue} from "../../../../../util/ref-service-mode-ui-util";
import {getRefServiceStatusFacetDisplayValue} from "../../../../../util/ref-service-status-facet-ui-util";
import {OpsLocationStatus} from "../../../../../model/ops-status";
import {useQuickPanelClose} from "../../QuickPanelContainer/QuickPanelContainer";
import {renderMapEntityAnchor} from "../map-entity-anchor";

export type OpsStatusInfoPanelProps = {
  site: OpsLocationStatus;
};

type CommonT = Parameters<typeof getRefServiceStatusDisplayValue>[1];

/** A site's ops status with every code translated, for the TW panels. */
export const toOpsStatusView = (status: OpsLocationStatus, t: CommonT): OpsStatusView => {
  const serviceTypes = Object.values(status.serviceTypes).map(serviceType => ({
    id: serviceType.serviceTypeId,
    name: serviceType.serviceTypeName,
    status: serviceType.overallStatus,
    label: getRefServiceStatusDisplayValue(serviceType.overallStatus, t),
    facets: Object.values(serviceType.facets ?? {}).map(facet => ({
      facet: facet.facet,
      facetLabel: getRefServiceStatusFacetDisplayValue(facet.facet, t),
      status: facet.status,
      label: getRefServiceStatusDisplayValue(facet.status, t),
      modes: (facet.modes ?? []).map(mode => ({
        mode: mode.mode,
        modeLabel: getRefServiceModeDisplayValue(mode.mode, t),
        status: mode.status,
        label: getRefServiceStatusDisplayValue(mode.status, t),
        asOf: mode.asOfDttm,
      })),
    })),
  }));
  return {
    status: status.overallStatus,
    label: getRefServiceStatusDisplayValue(status.overallStatus, t),
    asOf: latestOpsAsOf(serviceTypes),
    serviceTypes,
  };
};

export const OpsStatusInfoPanel = ({site}: OpsStatusInfoPanelProps) => {
  const {t} = useTranslation(["translation", "common"]);
  const navigate = useNavigate();
  const onClose = useQuickPanelClose();

  const status = useMemo(() => toOpsStatusView(site, t as unknown as CommonT), [site, t]);
  const events = useMemo(() => {
    const byId = new Map<string, string | undefined>();
    for (const row of site.rows ?? []) {
      if (row.serviceEventId) byId.set(row.serviceEventId, row.serviceEventName);
    }
    return [...byId].map(([id, name]) => ({id, name, type: "SVCEVENT"}));
  }, [site.rows]);

  return (
    <OpsStatusInfoPanelView
      site={{id: site.siteId, name: site.siteName, type: "SITE"}}
      latitude={site.latCoord}
      longitude={site.longCoord}
      status={status}
      events={events}
      renderEntity={renderMapEntityAnchor}
      onSetStatus={() => navigate("/location-statuses/form", {state: {formDefaults: {site: site.siteId}}})}
      onClose={onClose}/>
  );
};
